import { FC, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Paper from '@base/Paper/Paper';
import { Fonts, Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import usePreciseTimer from '@hooks/usePreciseTimer';
import { getShortFormTimeFormat } from '@utils/dates';
import { segment } from '@actions/analytics';
import { Banner_Banner_Viewed } from '@constants/segment';
import {
  OFFLINE_CENTRE_KNOW_MORE_CLICKED,
  OFFLINE_GOAL_UID,
  KNOW_MORE_FLOW_TYPE_WEB_HOMEPAGE,
  KNOW_MORE_FLOW_TYPE_WEB_SUBSCRIPTION
} from '@cont/OfflineCentres/constants';
import { useRouter } from 'next/router';

type ContainerProps = {
  isSmallBanner: boolean;
  isMobile?: boolean;
};

type ImageContainerProps = {
  isSmallBanner: boolean;
  imgSrc: string;
  isMobile?: boolean;
};

const Container = styled(Paper)<ContainerProps>`
  position: relative;
  border-radius: 8px;
  box-shadow: ${boxShadowGenerator()};
  padding: ${Spacings.SPACING_10B};
  display: flex;
  height: ${({ isSmallBanner }) => (isSmallBanner ? '324' : '476')}px;
  width: 1136px;
  top: -47px;
  margin: auto auto ${Spacings.SPACING_10B};

  ${({ isMobile }) =>
    isMobile &&
    `
    padding: ${Spacings.SPACING_4B};
    flex-direction: column;
    height: auto;
    width: auto;
    margin: 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
  `}
`;

const ImageContainer = styled.div<ImageContainerProps>`
  position: relative;
  height: ${({ isSmallBanner }) => (isSmallBanner ? '244px' : 'auto')};
  width: 440px;
  border-radius: 8px;
  margin: 0 ${Spacings.SPACING_10B} 0 0;
  cursor: pointer;
  background: ${({ imgSrc }) => `url('${imgSrc}')`};
  background-size: cover;
  background-position: center center;

  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
    height: auto;
    margin: 0 0 ${Spacings.SPACING_4B} 0;
    aspect-ratio: 2.25;
  `}
`;

type ContentDivProps = {
  justifyContent: string;
  isMobile?: boolean;
};

const ContentDiv = styled.div<ContentDivProps>`
  ${({ isMobile, justifyContent }) =>
    !isMobile &&
    `
    height: 100%;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: ${justifyContent};
  `}
`;

type TagProps = {
  color?: string;
  isMobile?: boolean;
};

const Tag = styled.div<TagProps>`
  font-weight: 700;
  font-size: ${Fonts.H7};
  color: ${({ color }) => color || 'FF9706'};
  margin-bottom: ${Spacings.SPACING_4B};

  span {
    margin-right: ${Spacings.SPACING_1B};
  }

  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: ${Fonts.EXTRA_SMALL};
    line-height: ${Fonts.H7};
    margin-bottom: ${Spacings.SPACING_2B};
  `}
`;

type isMobileProps = {
  isMobile?: boolean;
};

const Heading = styled.div<isMobileProps>`
  font-weight: 700;
  font-size: ${Fonts.H2};
  line-height: 120%;
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_4B};

  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: ${Fonts.H4};
    line-height: ${Fonts.H3};
    margin-bottom: ${Spacings.SPACING_2B};
  `}
`;

const SubHeading = styled.div<isMobileProps>`
  font-size: ${Fonts.H4};
  line-height: ${Fonts.H3};
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_6B};

  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: ${Fonts.H6};
    line-height: ${Fonts.H4};
    margin-bottom: ${Spacings.SPACING_3B};
  `}
`;

const Points = styled.div``;

const Point = styled.div<isMobileProps>`
  display: flex;
  margin-bottom: ${Spacings.SPACING_3B};

  &:last-of-type {
    margin-bottom: 0;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
    margin-bottom: ${Spacings.SPACING_2B};
  `}
`;

const PointText = styled.div<isMobileProps>`
  font-size: ${Fonts.H5};
  line-height: 22px;
  margin-left: ${Spacings.SPACING_2B};

  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: ${Fonts.H7};
    line-height: 18px;
  `}
`;

type OfferAlignProps = {
  alignOfferToRight: boolean;
  isMobile?: boolean;
};

const BottomDiv = styled.div<OfferAlignProps>`
  margin-top: ${Spacings.SPACING_6B};

  ${({ isMobile, alignOfferToRight }) =>
    isMobile &&
    `
    margin-top: ${Spacings.SPACING_5B};
    border-top: 1px solid var(--color-divider);
    padding-top: ${Spacings.SPACING_4B};
    align-items: center;
    display: ${alignOfferToRight ? 'flex' : 'block'};
  `}
`;

const PriceAndCTA = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Price = styled.div`
  margin-right: ${Spacings.SPACING_4B};
`;

const PreviousPrice = styled.div`
  font-weight: 600;
  font-size: ${Fonts.H7};
  line-height: ${Fonts.H5};
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_1B};
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
`;

const ActualPrice = styled.div`
  font-weight: 600;
  font-size: ${Fonts.H4};
  line-height: ${Fonts.H3};
  color: var(--color-text-primary);
`;

const StyledButton = styled(Button)<isMobileProps>`
  width: 185px;
  height: 48px;

  ${({ isMobile }) =>
    isMobile &&
    `
    min-width: 185px;
    flex: 1;
  `}
`;

const Offer = styled.div<OfferAlignProps>`
  width: 100%;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${Fonts.H7};
  line-height: ${Fonts.H5};
  color: var(--color-i-yellow-5);
  margin-top: ${Spacings.SPACING_4B};
  background: url('/static/images/presubscribed/generic-banner/offer-background.svg');
  background-repeat: no-repeat;
  background-size: cover;

  span {
    margin-left: 6px;
  }

  ${({ isMobile, alignOfferToRight }) =>
    isMobile &&
    `
    height: 32px;
    flex: 1;
    margin-top: ${alignOfferToRight ? '0' : Spacings.SPACING_4B};
  `}
`;

type CampaignBanner2Props = {
  banner: {
    bannerUid?: string;
    primaryImage?: {
      app: string;
      web: string;
      click_url: string;
    };
    primaryColor?: string;
    tagText?: string;
    tagIcon?: string;
    heading: string;
    subHeading?: string;
    info?: Array<{
      text: string;
      icon?: string;
    }>;
    slashText?: string;
    discountText?: string;
    finalText?: string;
    cta?: {
      url: string;
      text: string;
    };
    timeStripInfo?: {
      prefix: string;
      endTime: string;
    };
  };
  isMobile?: boolean;
};

const useOnScreen = (ref) => {
  const [isOnScreen, setIsOnScreen] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(([entry]) => {
      setIsOnScreen(entry.isIntersecting);
    });
  }, []);

  useEffect(() => {
    observerRef.current.observe(ref.current);

    return () => {
      observerRef.current.disconnect();
    };
  }, [ref]);

  return isOnScreen;
};

const oneDayInSecs = 24 * 60 * 60;

type TimeLeftProps = {
  timeRemaining: number;
};

const TimeLeft: FC<TimeLeftProps> = ({ timeRemaining }) => {
  const [time, setTime] = useState(timeRemaining);
  const handler = useCallback(() => {
    setTime((data) => data - 1);
  }, []);

  usePreciseTimer(handler, 1000, true);

  return <>{getShortFormTimeFormat(time)}</>;
};

const CampaignBanner2: FC<CampaignBanner2Props> = ({ banner, isMobile }) => {
  const router = useRouter();
  const { goalUID } = router.query;
  const ref = useRef(null);
  const isVisible = useOnScreen(ref);
  const [isEventTriggered, setIsEventTriggered] = useState(false);
  const bannerUids = ['4S0K37MS4E', 'S0OLXSF07P'];
  const isUnacademyCentresBanner = bannerUids.includes(banner.bannerUid);

  if (isVisible && isUnacademyCentresBanner && !isEventTriggered) {
    segment.track(Banner_Banner_Viewed, {
      banner_uid: banner.bannerUid,
      goal_uid: goalUID || OFFLINE_GOAL_UID
    });
    setIsEventTriggered(true);
  }
  const goToUrl = useCallback((url) => {
    if (isUnacademyCentresBanner) {
      const currentUrl = window.location.href;
      segment.track(OFFLINE_CENTRE_KNOW_MORE_CLICKED, {
        know_more_flow_type: currentUrl.endsWith('subscribe')
          ? KNOW_MORE_FLOW_TYPE_WEB_SUBSCRIPTION
          : KNOW_MORE_FLOW_TYPE_WEB_HOMEPAGE,
        goal_uid: goalUID || OFFLINE_GOAL_UID
      });
    }
    if (url) window.location.href = url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeRemaining = useMemo(() => {
    if (banner?.timeStripInfo?.endTime)
      return Math.round(
        (new Date(banner.timeStripInfo.endTime).getTime() -
          new Date().getTime()) /
          1000
      );
    return 0;
  }, [banner]);

  const getBackgroundImage = (): string => {
    if (isMobile) return banner.primaryImage.app;
    return `${banner.primaryImage.web}?w=440&auto=format,compress`;
  };

  return (
    <Container
      isSmallBanner={!(banner.finalText || banner.timeStripInfo || banner.cta)}
      isMobile={isMobile}
      ref={ref}
    >
      <ImageContainer
        isSmallBanner={
          !(banner.finalText || banner.timeStripInfo || banner.cta)
        }
        isMobile={isMobile}
        imgSrc={getBackgroundImage()}
        onClick={() => goToUrl(banner.primaryImage.click_url)}
      />
      <ContentDiv
        justifyContent={
          !banner.finalText && !banner.timeStripInfo
            ? 'center'
            : 'space-between'
        }
        isMobile={isMobile}
      >
        <div>
          {banner.tagText && (
            <Tag color={banner.primaryColor} isMobile={isMobile}>
              <span>{banner.tagText}</span>
              <Image
                src={banner.tagIcon || '/presubscribed/generic-banner/star.svg'}
                height={12}
                width={12}
                alt="star"
              />
            </Tag>
          )}
          {banner.heading && (
            <Heading isMobile={isMobile}>{banner.heading}</Heading>
          )}
          {banner.subHeading && (
            <SubHeading isMobile={isMobile}>{banner.subHeading}</SubHeading>
          )}
          {banner.info && banner.info.length > 0 && (
            <Points>
              {banner.info.map(({ text, icon }) => (
                <Point key={text} isMobile={isMobile}>
                  <Image
                    src={icon || '/presubscribed/generic-banner/point.svg'}
                    height={20}
                    width={20}
                    alt="point"
                  />
                  <PointText isMobile={isMobile}>{text}</PointText>
                </Point>
              ))}
            </Points>
          )}
        </div>
        {(banner.finalText || banner.timeStripInfo || banner.cta) && (
          <BottomDiv
            alignOfferToRight={banner.finalText && !banner.cta}
            isMobile={isMobile}
          >
            <PriceAndCTA>
              {banner.finalText && (
                <Price>
                  <PreviousPrice>
                    <StrikeThrough>{banner.slashText}</StrikeThrough>
                    &nbsp;{banner.discountText}
                  </PreviousPrice>
                  <ActualPrice>{banner.finalText}</ActualPrice>
                </Price>
              )}
              {banner.cta && (
                <StyledButton
                  // @ts-ignore
                  label={banner.cta.text}
                  isMobile={isMobile}
                  variant="filled"
                  color="black"
                  onClick={() => goToUrl(banner.cta.url)}
                />
              )}
            </PriceAndCTA>
            {banner.timeStripInfo && timeRemaining > 0 && (
              <Offer alignOfferToRight={!banner.cta} isMobile={isMobile}>
                <Image
                  src="/presubscribed/generic-banner/offer.svg"
                  height={12}
                  width={12}
                  alt="offer"
                />
                <span>
                  {banner.timeStripInfo.prefix}&nbsp;
                  {timeRemaining > oneDayInSecs ? (
                    `${Math.floor(timeRemaining / oneDayInSecs)} days`
                  ) : (
                    <TimeLeft timeRemaining={timeRemaining} />
                  )}
                </span>
              </Offer>
            )}
          </BottomDiv>
        )}
      </ContentDiv>
    </Container>
  );
};

export default CampaignBanner2;
